// src/services/tutorService.js

/**
 * Servicio Tutor para la Distribución y Seguimiento de Temas de Estudio
 * Réplica de la lógica de TutorDistributionUseCase.kt de la app Android.
 */

// Mapeo numérico a días de la semana compatible con JS Date.getDay() (0 = Domingo, 1 = Lunes...)
// En Android usan java.time.DayOfWeek (1 = Lunes, 7 = Domingo). Ajustamos para usar 1-7 donde 7 es Domingo.

const getAvailableDays = (diasDisponiblesStr) => {
    if (!diasDisponiblesStr || diasDisponiblesStr.trim() === '') {
        return [1, 2, 3, 4, 5, 6, 7]; // Todos los días por defecto (1=Lunes, 7=Domingo)
    }
    try {
        return diasDisponiblesStr.split(',').map(d => parseInt(d.trim()));
    } catch (e) {
        return [1, 2, 3, 4, 5, 6, 7];
    }
};

const getDayOfWeekAndroidStyle = (dateObj) => {
    const day = dateObj.getDay(); // 0 (Dom) - 6 (Sab)
    return day === 0 ? 7 : day;
};

const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
};

// Calcula cuántos días hábiles quedan entre startDate y endDate
const getWorkingDays = (startDateStr, endDateStr, availableDays) => {
    const days = [];
    let current = new Date(startDateStr);
    const end = new Date(endDateStr);
    
    // Normalizar a horas cero para comparación justa
    current.setHours(0,0,0,0);
    end.setHours(0,0,0,0);

    // Evitar loops infinitos en caso de fechas inválidas
    let safetyCounter = 0;
    while (current <= end && safetyCounter < 1000) {
        const dayOfWeek = getDayOfWeekAndroidStyle(current);
        if (availableDays.includes(dayOfWeek)) {
            days.push(current.toISOString().split('T')[0]);
        }
        current = addDays(current, 1);
        safetyCounter++;
    }
    return days;
};

/**
 * Se llama cuando se crea el plan o cuando se fuerza una redistribución.
 */
export const distributeInitialTopics = (plan, temas) => {
    const todayStr = new Date().toISOString().split('T')[0];
    const fechaInicio = plan.fechaInicio || todayStr;
    
    // Si no hay fechaExamen, por defecto es un mes después
    let fechaExamen = plan.fechaExamen;
    if (!fechaExamen) {
        const nextMonth = new Date();
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        fechaExamen = plan.fechaFin || nextMonth.toISOString().split('T')[0];
    }

    const availableDays = getAvailableDays(plan.diasDisponibles);
    const workingDays = getWorkingDays(fechaInicio, fechaExamen, availableDays);

    if (workingDays.length === 0) {
        return { success: false, message: "No hay días hábiles disponibles antes del examen.", updatedTemas: temas };
    }

    // Pendientes
    const temasToDistribute = temas.filter(t => t.estado === 'PENDIENTE' || !t.estado);
    const temasPorDia = Math.ceil(temasToDistribute.length / workingDays.length);
    const limiteDiario = plan.limiteTemasDiarios || 5;

    if (temasPorDia > limiteDiario) {
        return { 
            success: false, 
            message: `Punto de no retorno: Carga inicial de ${temasPorDia} supera el límite de ${limiteDiario} diarios.`, 
            updatedTemas: temas, 
            isCriticalDelay: true, 
            cargaDiariaCalculada: temasPorDia 
        };
    }

    const updatedTemas = [];
    let dayIndex = 0;
    let temasInCurrentDay = 0;

    for (const tema of temas) {
        const isPendiente = tema.estado === 'PENDIENTE' || !tema.estado;
        if (!isPendiente) {
            updatedTemas.push(tema);
            continue;
        }

        const assignDate = workingDays[dayIndex];
        updatedTemas.push({ ...tema, fechaAsignada: assignDate, estado: 'PENDIENTE' });

        temasInCurrentDay++;
        if (temasInCurrentDay >= temasPorDia) {
            temasInCurrentDay = 0;
            if (dayIndex < workingDays.length - 1) {
                dayIndex++;
            }
        }
    }

    return { 
        success: true, 
        message: `Distribución inicial exitosa a ${temasPorDia} temas por día.`, 
        updatedTemas, 
        isCriticalDelay: false, 
        cargaDiariaCalculada: temasPorDia 
    };
};

/**
 * Verifica si hay temas vencidos y los redistribuye si es necesario.
 */
export const recalculateIfBehind = (plan, temas) => {
    const todayStr = new Date().toISOString().split('T')[0];
    
    let fechaExamen = plan.fechaExamen;
    if (!fechaExamen) {
        const nextMonth = new Date();
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        fechaExamen = plan.fechaFin || nextMonth.toISOString().split('T')[0];
    }

    // Temas que estaban asignados para ayer o antes y siguen pendientes
    const temasVencidos = temas.filter(t => {
        const isPendiente = t.estado === 'PENDIENTE' || !t.estado;
        return isPendiente && t.fechaAsignada && t.fechaAsignada < todayStr;
    });

    if (temasVencidos.length === 0) {
        return { success: true, message: "Vas al día. No es necesario recalcular.", updatedTemas: temas };
    }

    // Está atrasado. Redistribuimos TODOS los temas pendientes desde HOY hasta el EXAMEN.
    const availableDays = getAvailableDays(plan.diasDisponibles);
    const remainingWorkingDays = getWorkingDays(todayStr, fechaExamen, availableDays);

    if (remainingWorkingDays.length === 0) {
        return { 
            success: false, 
            message: "Punto de No Retorno: Examen inminente y hay temas atrasados.", 
            updatedTemas: temas, 
            isCriticalDelay: true, 
            cargaDiariaCalculada: 999 
        };
    }

    const allPendientes = temas.filter(t => t.estado === 'PENDIENTE' || !t.estado);
    const temasPorDia = Math.ceil(allPendientes.length / remainingWorkingDays.length);
    const limiteDiario = plan.limiteTemasDiarios || 5;

    if (temasPorDia > limiteDiario) {
        return { 
            success: false, 
            message: `Punto de No Retorno: Reagendar o usar modo supervivencia (${temasPorDia} temas/día).`, 
            updatedTemas: temas, 
            isCriticalDelay: true, 
            cargaDiariaCalculada: temasPorDia 
        };
    }

    const updatedTemas = [];
    let dayIndex = 0;
    let temasInCurrentDay = 0;

    for (const tema of temas) {
        const isPendiente = tema.estado === 'PENDIENTE' || !tema.estado;
        if (!isPendiente) {
            updatedTemas.push(tema);
            continue;
        }

        const assignDate = remainingWorkingDays[dayIndex];
        updatedTemas.push({ ...tema, fechaAsignada: assignDate });

        temasInCurrentDay++;
        if (temasInCurrentDay >= temasPorDia) {
            temasInCurrentDay = 0;
            if (dayIndex < remainingWorkingDays.length - 1) {
                dayIndex++;
            }
        }
    }

    return { 
        success: true, 
        message: `Te atrasaste. Carga recalculada a ${temasPorDia} temas diarios.`, 
        updatedTemas, 
        isCriticalDelay: false, 
        cargaDiariaCalculada: temasPorDia 
    };
};

export const getPerformanceFeedback = (aprovechamiento) => {
    if (aprovechamiento === 0 || isNaN(aprovechamiento)) return "";
    
    if (aprovechamiento >= 9.0) return "¡Nivel de excelencia! Estás más que listo para el ascenso. 🏆";
    if (aprovechamiento >= 8.0) return "¡Muy buen rendimiento! Mantén este ritmo y asegurarás tu plaza. 🌟";
    if (aprovechamiento >= 7.0) return "Vas por buen camino, pero no te confíes. Refuerza los temas con dudas. ✅";
    if (aprovechamiento >= 5.0) return "Cuidado: Tu promedio está en zona de riesgo. Necesitas dedicarle más tiempo al repaso. ⚠️";
    return `¡Alerta! Tu rendimiento es insuficiente (${aprovechamiento.toFixed(1)}). Sugiero volver a estudiar las bases de estos temas. 🛑`;
};
